import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import ConcalLogo from '../../components/ConcalLogo';
import MetricsCard from './components/MetricsCard';
import ActivityTable from './components/ActivityTable';
import QuickStatsBar from './components/QuickStatsBar';
import RecentEmailTemplates from './components/RecentEmailTemplates';
import authService from '../../utils/authService';

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading } = useAuth();

  useEffect(() => {
    // Log dashboard access
    if (user && !loading) {
      authService?.logPlatformUsage('dashboard_accessed');
    }
  }, [user, loading]);

  useEffect(() => {
    // Redirect developers to their dashboard
    if (userProfile?.role === 'developer') {
      navigate('/developer-dashboard');
    }
  }, [userProfile, navigate]);

  const handleUploadContract = () => {
    navigate('/contract-upload');
  };

  const handleViewHistory = () => {
    navigate('/contract-history');
  };

  // Show loading state with dark theme
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <main className="pt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="animate-pulse space-y-8">
              <div className="space-y-4">
                <div className="h-8 bg-muted rounded-lg w-80"></div>
                <div className="h-4 bg-muted rounded-lg w-96"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1, 2, 3]?.map(i => (
                  <div key={i} className="h-40 bg-muted rounded-xl"></div>
                ))}
              </div>
              <div className="h-96 bg-muted rounded-xl"></div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // Show AI-powered mode for all users (no database dependency)
  const renderMainContent = () => (
    <>
      {/* Page Header with Login → Upload Flow */}
      <div className="text-center mb-12">
        <div className="flex items-center justify-center mb-6">
          <ConcalLogo size="xl" className="w-auto h-24" />
        </div>
        
        <div className="max-w-3xl mx-auto">
          <p className="text-muted-foreground text-body text-xl mb-8 leading-relaxed">
            AI-powered contract analysis without database dependencies - Pure OpenAI processing
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-8">
            <Button
              variant="default"
              size="lg"
              iconName="Upload"
              iconPosition="left"
              iconSize={20}
              onClick={handleUploadContract}
              className="w-full sm:w-auto text-lg px-8 py-4"
            >
              Upload Contract
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="History"
              iconPosition="left"
              iconSize={20}
              onClick={handleViewHistory}
              className="w-full sm:w-auto text-lg px-8 py-4"
            >
              View History
            </Button>
          </div>
        </div>
      </div>

      {/* AI Processing Status Banner */}
      <div className="apple-card p-6 mb-8 bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-primary/20 rounded-xl flex items-center justify-center flex-shrink-0">
              <span className="text-primary font-bold text-xl">AI</span>
            </div>
            <div className="min-w-0 flex-1">
              <h3 className="text-title font-semibold text-foreground text-lg">
                Database-Free Mode Active
              </h3>
              <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground mt-1">
                <span className="capitalize font-medium text-primary">OpenAI Processing</span>
                <span>•</span>
                <span className="truncate">No Database Errors</span>
                <span>•</span>
                <span className="capitalize">Real-time Analysis</span>
              </div>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
            <span className="text-sm text-success font-medium">AI Ready</span>
          </div>
        </div>
      </div>

      {/* Quick Stats Bar */}
      <div className="mb-8">
        <QuickStatsBar />
      </div>

      {/* Main Content Grid */}
      <div className="dashboard-grid mb-8">
        {/* Left Section - Activity Table */}
        <div className="w-full">
          <ActivityTable />
        </div>

        {/* Right Section - Metrics & Email Templates */}
        <div className="w-full space-y-6">
          {/* Summary Metrics Cards */}
          <div className="metrics-card-container">
            <MetricsCard
              title="OpenAI Status"
              value="Active"
              subtitle="Ready for processing"
              icon="Cpu"
              color="success"
              trend="up"
              trendValue="100%"
            />
            <MetricsCard
              title="Database Status"
              value="Bypassed"
              subtitle="Using local storage"
              icon="Database"
              color="warning"
              trend="neutral"
              trendValue="Offline"
            />
            <MetricsCard
              title="Processing Mode"
              value="Direct"
              subtitle="No 400 errors"
              icon="Zap"
              color="primary"
              trend="up"
              trendValue="Fixed"
            />
          </div>

          {/* Recent Email Templates */}
          <RecentEmailTemplates />
        </div>
      </div>

      {/* Welcome Message for New Users */}
      <div className="welcome-section p-8 text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-primary/20 to-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <svg className="w-8 h-8 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-display text-2xl font-bold text-foreground mb-3">
          AI-Powered Contract Processing
        </h3>
        <p className="text-muted-foreground text-body mb-8 max-w-2xl mx-auto text-lg leading-relaxed">
          Experience real OpenAI contract analysis without database dependencies. Upload contracts for immediate AI processing 
          and get structured results directly without any 400 errors.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            variant="default"
            iconName="Upload"
            iconPosition="left"
            iconSize={18}
            onClick={handleUploadContract}
            className="w-full sm:w-auto"
          >
            Try AI Processing
          </Button>
          <Button
            variant="ghost"
            iconName="HelpCircle"
            iconPosition="left"
            iconSize={18}
            className="w-full sm:w-auto"
          >
            How It Works
          </Button>
        </div>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {renderMainContent()}
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
